import { Injectable } from '@angular/core';
import { LeadSetup } from '@wb/core/interfaces/setup';
import { Visitor } from '@wb/core/interfaces/visitor';
import { VisitorName } from '@wb/core/interfaces/visitor-name';
import { Mutation } from 'apollo-angular';
import gql from 'graphql-tag';

export interface AccountUpdateInput {
  account?: string;
  address?: string;
  city?: string;
  state?: string;
  postalcode?: string;
  county?: string;
  country?: string;
  dealer_logo?: string;
  dealer_logo_key?: string;
  integrations?: string;
  integration_status?: string;
  configuration?: string;
  demo?: string;
  oem_type?: string;
  pricing?: string;
  landing_page?: string;
  atc_quote_type?: string;
  loan_provider?: string;
  routeone_id?: string;
  dealertrack_id?: string;
  routeone_status?: string;
  dealertrack_status?: string;
  loan_provider_status?: string;
  cdk_last_import?: string;
  cdk_last_hit?: string;
  created_at?: string;
  updated_at?: string;
}

export interface AccountIntegrationInput {
  uid: string;
  accountInput: AccountUpdateInput;
  accountSettingsInput: AccountSettingsUpdateInput[];
}

export interface AccountBackEndInput {
  uid: string;
  accountPricingInput: AccountPricingUpdateInput[];
  accountSettingsInput: AccountSettingsUpdateInput[];
  accountLendersInput: AccountLendersUpdateInput[];
}

export interface AccountUpdatePricingInput {
  uid: string;
  accountPricingInput: AccountPricingUpdateInput[];
}

export interface AccountUpdatePricingResponse {
  updateAccountPricing: {
    code: string;
    success: boolean;
    message: string;
  };
}

export interface AccountIntegrationResponse {
  updateAccount: {
    code: string;
    success: boolean;
    message: string;
  };
  updateAccountSettings: {
    code: string;
    success: boolean;
    message: string;
  };
}

export interface AccountBackEndResponse {
  updateAccountPricing: {
    code: string;
    success: boolean;
    message: string;
  };
  updateAccountSettings: {
    code: string;
    success: boolean;
    message: string;
  };
  updateAccountPacks: {
    code: string;
    success: boolean;
    message: string;
  };
  updateAccountLenders: {
    code: string;
    success: boolean;
    message: string;
  };
}

export interface AccountInput {
  account?: string;
  address?: string;
  city?: string;
  state?: string;
  postalcode?: string;
  county?: string;
  country?: string;
  dealer_logo?: string;
  dealer_logo_key?: string;
  integrations?: string;
  integration_status?: string;
  configuration?: string;
  demo?: string;
  oem_type?: string | null;
  pricing?: string;
  landing_page?: string;
  atc_quote_type?: string;
  routeone_id?: string;
  dealertrack_id?: string;
  loan_provider_status?: string;
  loan_provider?: string;
  cdk_last_import?: string;
  cdk_last_hit?: string;
}

export interface AccountUpdate {
  uid?: string;
  input?: AccountInput;
}

export interface CreateAccountResponse {
  insertAccount: {
    code: string;
    success: boolean;
    message: string;
    account: {
      uid: string;
      account: string;
    };
  };
}

export interface UpdateAccountResponse {
  updateAccount: {
    code: string;
    success: boolean;
    message: string;
  };
}

export interface ThemeInput {
  buttonTheme?: string;
  clientTheme?: string;
}

export interface AccountThemesUpdateInput {
  uid: string;
  input: ThemeInput;
}

export interface UpdateAccountThemesResponse {
  updateAccountThemes: {
    code: string;
    success: boolean;
    message: string;
  };
}

export interface AccountInsert {
  account: string;
  address?: string;
  city?: string;
  state: string;
  postalcode?: string;
  county?: string;
  country: string;
  dealer_logo?: string;
  dealer_logo_key?: string;
  integrations?: string;
  integration_status?: string;
  configuration?: string;
  demo?: string;
  oem_type?: string;
  landing_page?: string;
  atc_quote_type?: string;
  routeone_id?: string;
  dealertrack_id?: string;
  loan_provider_status?: string;
  loan_provider?: string;
  cdk_last_import?: string;
  cdk_last_hit?: string;
  last_updated?: string;
  created_at?: string;
  updated_at?: string;
}

export interface AccountInsertInput {
  input: AccountInsert;
}

@Injectable({
  providedIn: 'root',
})
export class CreateAccountGQL extends Mutation<CreateAccountResponse, AccountInsertInput> {
  document = gql(`
  mutation insertAccount($input: ModelAccountInsertInput) {
    insertAccount(input: $input) {
        success
        message
        account {
            uid
            account
        }
    }
}
`);
}

@Injectable({
  providedIn: 'root',
})
export class UpdateAccountGQL extends Mutation<UpdateAccountResponse, AccountUpdate> {
  document = gql(`
    mutation updateAccount($uid: ID!, $input: ModelAccountUpdateInput) {
      updateAccount (uid: $uid, input: $input) {
        code
        success
        message
      }
    }
  `);
}

export interface AccountPricingUpdateInput {
  key: string;
  value: string;
}

export interface AccountSettingsUpdateInput {
  key: string;
  value: string;
}

export interface AccountLendersUpdateInput {
  lender: string;
  chargeback: number;
}

export interface AccountHoursUpdateInput {
  day: string;
  open: number;
  close: number;
}

export interface AccountDealerInformationInput {
  uid: string;
  accountSettingsInput: AccountSettingsUpdateInput[];
  accountHoursInput: AccountHoursUpdateInput[];
  accountInput: AccountInput;
}

export interface AccountDealerInformationResponse {
  updateAccountSettings: {
    code: string;
    success: boolean;
    message: string;
  };
  updateAccountHours: {
    code: string;
    success: boolean;
    message: string;
  };
  updateAccount: {
    code: string;
    success: boolean;
    message: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class AccountDealerInformationGQL extends Mutation<
  AccountDealerInformationResponse,
  AccountDealerInformationInput
> {
  document = gql(`
    mutation updateAccountDealerInformation(
      $uid: ID!,
      $accountSettingsInput: [ModelAccountSettingsUpdateInput],
      $accountHoursInput: [ModelAccountHoursUpdateInput],
      $accountInput: ModelAccountUpdateInput
    ) {
      updateAccount(uid: $uid, input: $accountInput) {
        success
        message
        code
      }
      updateAccountSettings(uid: $uid, input: $accountSettingsInput) {
        success
        message
        code
      }
      updateAccountHours(uid: $uid, input: $accountHoursInput) {
        success
        message
        code
      }
    }
    `);
}

export interface AccountHoursUpdateResponse {
  updateAccountHours: {
    code: string;
    success: boolean;
    message: string;
  };
}

export interface AccountHoursInput {
  uid: string;
  input: AccountHoursUpdateInput[];
}

@Injectable({
  providedIn: 'root',
})
export class AccountHoursUpdateGQL extends Mutation<AccountHoursUpdateResponse, AccountHoursInput> {
  document = gql(`
    mutation updateAccountHours($uid: ID!, $input: [ModelAccountHoursUpdateInput]) {
      updateAccountHours(uid: $uid, input: $input) {
        success
        message
        code
      }
    }
  `);
}

@Injectable({
  providedIn: 'root',
})
export class AccountIntegrationsUpdateGQL extends Mutation<AccountIntegrationResponse, AccountIntegrationInput> {
  document = gql(`
    mutation updateAccountIntegration(
      $uid: ID!,
      $accountInput: ModelAccountUpdateInput
      $accountSettingsInput: [ModelAccountSettingsUpdateInput],
    ) {
      updateAccount(uid: $uid, input: $accountInput) {
        success
        message
        code
      }
      updateAccountSettings(uid: $uid, input: $accountSettingsInput) {
        success
        message
        code
      }
    }
  `);
}

@Injectable({
  providedIn: 'root',
})
export class AccountBackEndUpdateGQL extends Mutation<AccountBackEndResponse, AccountBackEndInput> {
  document = gql(`
    mutation updateAccountBackEnd(
      $uid: ID!,
      $accountPricingInput: [ModelAccountPricingUpdateInput],
      $accountSettingsInput: [ModelAccountSettingsUpdateInput],
      $accountLendersInput: [ModelAccountLendersUpdateInput],
    ) {
      updateAccountPricing(uid: $uid, input: $accountPricingInput) {
        success
        message
        code
      }
      updateAccountSettings(uid: $uid, input: $accountSettingsInput) {
        success
        message
        code
      }
      updateAccountLenders(uid: $uid, input: $accountLendersInput) {
        success
        message
        code
      }
    }
  `);
}

@Injectable({
  providedIn: 'root',
})
export class AccountPricingUpdateGQL extends Mutation<AccountUpdatePricingResponse, AccountUpdatePricingInput> {
  document = gql(`
    mutation updateAccountPricing(
      $uid: ID!,
      $accountPricingInput: [ModelAccountPricingUpdateInput],
    ) {
      updateAccountPricing(uid: $uid, input: $accountPricingInput) {
        success
        message
        code
      }
    }
  `);
}

@Injectable({
  providedIn: 'root',
})
export class AccountThemesUpdateGQL extends Mutation<UpdateAccountThemesResponse, AccountThemesUpdateInput> {
  document = gql(`
    mutation updateAccountThemes(
      $uid: ID!
      $input: ModelAccountThemeInput!
    ) {
      updateAccountThemes(uid: $uid, input: $input) {
        code
        success
        message
      }
    }
  `);
}

export interface AccountSettingsUpdateResponse {
  updateAccountSettings: {
    code: string;
    success: boolean;
    message: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class AccountSettingsUpdateGQL extends Mutation<
  AccountSettingsUpdateResponse,
  { uid: string; accountSettingsInput: AccountSettingsUpdateInput[] }
> {
  document = gql(`
    mutation updateAccountSettings(
      $uid: ID!,
      $accountSettingsInput: [ModelAccountSettingsUpdateInput],
    ) {
      updateAccountSettings(uid: $uid, input: $accountSettingsInput) {
        success
        message
        code
      }
    }
  `);
}

export interface AccountMappingInput {
  parentUid: string;
  accountUid: string;
  map?: string;
}

export interface UpdateAccountMappingResponse {
  updateAccountMapping: {
    code: string;
    success: boolean;
    message: string;
  };
}

export interface InsertAccountMappingResponse {
  insertAccountMapping: {
    code: string;
    success: boolean;
    message: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class UpdateAccountMappingGQL extends Mutation<UpdateAccountMappingResponse, AccountMappingInput> {
  document = gql(`
    mutation updateAccountMapping(
      $parentUid: String!,
      $accountUid: String!,
      $map: String
    ) {
      updateAccountMapping(parentUid: $parentUid, accountUid: $accountUid, map: $map) {
        code
        success
        message
      }
    }
  `);
}

@Injectable({
  providedIn: 'root',
})
export class InsertAccountMappingGQL extends Mutation<InsertAccountMappingResponse, AccountMappingInput> {
  document = gql(`
    mutation insertAccountMapping(
      $parentUid: String!,
      $accountUid: String!,
      $map: String
    ) {
      insertAccountMapping(parentUid: $parentUid, accountUid: $accountUid, map: $map) {
        code
        success
        message
      }
    }
  `);
}

interface DeleteAccountMappingResponse {
  deleteAccountMapping: {
    code: string;
    success: boolean;
    message: string;
  };
}

interface DeleteAccountMappingInput {
  parentUid: string;
  accountUid: string;
}

@Injectable({
  providedIn: 'root',
})
export class DeleteAccountMappingGQL extends Mutation<DeleteAccountMappingResponse, DeleteAccountMappingInput> {
  document = gql(`
    mutation deleteAccountMapping(
      $parentUid: String!,
      $accountUid: String!,
    ) {
      deleteAccountMapping(parentUid: $parentUid, accountUid: $accountUid) {
        code
        success
        message
      }
    }
  `);
}

export interface InsertAccountImportProfileResponse {
  createAccountImportProfiles: {
    code: string;
    success: boolean;
    message: string;
    accountImportProfiles: {
      uid: string;
      account_id: number;
      import_profile_id: number;
      shared_profile_id: string;
      ftp_user: string;
      dir: string;
      primary: boolean;
      importProfileName: string;
      importProfileUid: string;
    };
  };
}

export interface InsertAccountImportProfileInput {
  accountUid: string;
  importProfileUid: string;
  sharedProfileId: string;
  ftpUser: string;
  type: string;
  dir: string;
}

@Injectable({
  providedIn: 'root',
})
export class InsertAccountImportProfileGQL extends Mutation<
  InsertAccountImportProfileResponse,
  InsertAccountImportProfileInput
> {
  document = gql(`
    mutation insertAccountImportProfile($accountUid: String!, $importProfileUid: String!, $sharedProfileId: String!, $ftpUser: String!, $type: String!, $dir: String!) {
      insertAccountImportProfile(accountUid: $accountUid, importProfileUid: $importProfileUid, sharedProfileId: $sharedProfileId, ftpUser: $ftpUser, type: $type, dir: $dir) {
        code
        success
        message
      }
    }
  `);
}

export interface UpdateAccountImportProfileResponse {
  updateAccountImportProfiles: {
    code: string;
    success: boolean;
    message: string;
  };
}

export interface UpdateAccountImportProfileInput {
  uid: string;
  importProfileUid: string;
  sharedProfileId: string;
}

@Injectable({
  providedIn: 'root',
})
export class UpdateAccountImportProfileGQL extends Mutation<
  UpdateAccountImportProfileResponse,
  UpdateAccountImportProfileInput
> {
  document = gql(`
    mutation updateAccountImportProfile($uid: String!, $importProfileUid: String!, $sharedProfileId: String!) {
      updateAccountImportProfile(uid: $uid, importProfileUid: $importProfileUid, sharedProfileId: $sharedProfileId) {
        code
        success
        message
      }
    }
  `);
}

export interface DeleteAccountImportProfileResponse {
  deleteAccountImportProfile: {
    code: string;
    success: boolean;
    message: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class DeleteAccountImportProfileGQL extends Mutation<DeleteAccountImportProfileResponse, { uid: string }> {
  document = gql(`
    mutation deleteAccountImportProfile($uid: ID!) {
      deleteAccountImportProfile(uid: $uid) {
        code
        success
        message
      }
    }
  `);
}

interface DeleteAccountResponse {
  deleteAccount: {
    code: string;
    success: boolean;
    message: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class DeleteAccountGQL extends Mutation<DeleteAccountResponse, { uid: string }> {
  document = gql(`
    mutation deleteAccount($uid: ID!) {
      deleteAccount(uid: $uid) {
        code
        success
        message
      }
    }
  `);
}

export interface InsertVisitorResponse {
  insertVisitor: {
    code: string;
    message: string;
    success: boolean;
    visitor: Visitor;
  };
}

@Injectable({
  providedIn: 'root',
})
export class InsertVisitorGQL extends Mutation<InsertVisitorResponse> {
  document = gql(`
    mutation insertVisitor($input: ModelVisitorInsertInput) {
      insertVisitor(input: $input) {
        code
        message
        success
        visitor {
          uid
        }
      }
    }
  `);
}

export interface InsertVisitorNameResponse {
  insertVisitorName: {
    code: string;
    message: string;
    success: boolean;
    visitorName: VisitorName;
  };
}

@Injectable({
  providedIn: 'root',
})
export class InsertVisitorNameGQL extends Mutation<InsertVisitorNameResponse> {
  document = gql(`
    mutation insertVisitorName($visitor_uid: String, $input: ModelVisitorNameInsertInput) {
      insertVisitorName(
        visitor_uid: $visitor_uid
        input: $input
      ) {
        code
        message
        success
        visitorName {
          first_name,
          last_name,
          phone_number,
          email_address
        }
      }
    }
  `);
}

interface DeleteAccountLeadsByVisitorUidResponse {
  deleteAccountLeadsByVisitorUid: {
    code: string;
    success: boolean;
    message: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class DeleteAccountLeadsByVisitorUidGQL extends Mutation<
  DeleteAccountLeadsByVisitorUidResponse,
  { accountUid: string; visitorUid: string }
> {
  document = gql(`
    mutation deleteAccountLeadsByVisitorUidMapping(
      $accountUid: ID!,
      $visitorUid: ID!,
    ) {
      deleteAccountLeadsByVisitorUid(accountUid: $accountUid, visitorUid: $visitorUid) {
        code
        success
        message
      }
    }
  `);
}

export interface SetupLeadResponse {
  code: String;
  success: Boolean;
  message: String;
  leadSetup: LeadSetup;
}

@Injectable({
  providedIn: 'root',
})
export class SetupLeadGQL extends Mutation<SetupLeadResponse> {
  document = gql(`
    mutation leadSetup($input: LeadSetup) {
      leadSetup(input: $input) {
        code
        message
        success
        body {
          lead {
            uid
            visitor {
              uid
            }
          }
         }
      }
    }
  `);
}

export interface NetdeskerPiiResponse {
  code: String;
  success: Boolean;
  message: String;
}

@Injectable({
  providedIn: 'root',
})
export class NetdeskerPiiGQL extends Mutation<NetdeskerPiiResponse> {
  document = gql(`
    mutation updateVisitorPiis($visitor_uid: ID!, $input: [PiiInsert]) {
      updateVisitorPiis(visitor_uid: $visitor_uid, input: $input) {
        code
        message
        success
      }
    }
  `);
}

export interface UpdateAccountAggregatorAssertionIdResponse {
  code: String;
  success: Boolean;
  message: String;
}

export interface UpdateAccountAggregatorAssertionIdInput {
  parentUid: String;
  update: ModelAccountAggregatorUpdateInput[];
}

export interface ModelAccountAggregatorUpdateInput {
  childUid: String;
  assertionId: Boolean | null;
}

@Injectable({
  providedIn: 'root',
})
export class UpdateAccountAggregatorAssertionId extends Mutation<
  UpdateAccountAggregatorAssertionIdResponse,
  UpdateAccountAggregatorAssertionIdInput
> {
  document = gql(`
    mutation updateAccountAggregatorAssertionId($parentUid: String!, $update: [ModelAccountAggregatorUpdateInput]) {
      updateAccountAggregatorAssertionId(parentUid: $parentUid, update: $update) {
        code
        message
        success
      }
    }
  `);
}

export interface PublishAccountConfigResponse {
  code: String;
  success: Boolean;
  message: String;
}

@Injectable({
  providedIn: 'root',
})
export class PublishAccountConfigGQL extends Mutation<PublishAccountConfigResponse> {
  document = gql(`
    mutation publishAccountConfig($uid: ID!) {
      publishAccountConfig (uid: $uid) {
        code
        success
        message
      }
    }
  `);
}
